import {
  doc,
  getDoc,
  setDoc,
  DocumentReference,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
// Import db and storage from main_config instead of firebase.ts
import { db, FirebaseTimestamp } from '@/js/external-js/config/main_config';
import { getAuthUser } from '@/lib/authState';
import { getAllUserRoles, getISMSRoles } from './rolesService';
import MCMService from '@/services/mcmService';

/**
 * Gets the name of the first Business Sponsor user
 * @returns Promise resolving to the Business Sponsor's name or empty string if not found
 */
async function getBusinessSponsorName(): Promise<string> {
  try {
    const userRoles = await getAllUserRoles();
    const businessSponsor = userRoles.find(role => 
      role.ismsRole?.toLowerCase() === 'business sponsor'
    );

    console.log('[soaService] Business Sponsor:', businessSponsor);
    
    if (businessSponsor) {
      return businessSponsor.userId;
    }
    console.warn('[soaService] No Business Sponsor role found');
    return '';
  } catch (error) {
    console.error('[soaService] Error getting Business Sponsor:', error);
    return '';
  }
}

import {
  Control,
  Framework,
  FrameworkStats,
  SOAData,
  VersionInfo,
  VersionHistoryEntry,
  emptySOAData
} from '@/types/soa';

/**
 * Fetches frameworks data from Firestore
 * @param companyID The company ID to fetch frameworks for
 * @returns Promise containing an array of frameworks
 */
export async function fetchFrameworksData(companyID: string, isModuleEnabled: (moduleName: string) => boolean): Promise<Framework[]> {
  try {
    const frameworksData: Framework[] = [];
    const promises = [];

    if (isModuleEnabled('ISO 27001')) {
      console.log('[soaService.tsx] ISO 27001 module is enabled')
      // ISO 27001 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('iso270012022AnnexAControls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const iso27001Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] ISO 27001 Controls:', iso27001Controls);
              frameworksData.push({
                id: 'iso27001',
                name: 'ISO 27001',
                description: 'Information Security Management System standard',
                controlCount: iso27001Controls
              });
            }
          })
      );
    } else {
      console.log('[soaService.tsx] ISO 27001 module is disabled')
    }

    if (isModuleEnabled('qgov-is18')) {
      console.log('[soaService.tsx] QGOV IS18 module is enabled')
      // QGOV IS18 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('qgov-is18')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const qgovIs18Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] QGOV IS18 Controls:', qgovIs18Controls);
              frameworksData.push({
                id: 'qgov-is18',
                name: 'QGOV IS18',
                description: 'QGOV IS18 standard',
                controlCount: qgovIs18Controls
              });
            }
          })
      );
    } else {
      console.log('[soaService.tsx] QGOV IS18 module is disabled')
    }

    if (isModuleEnabled('SOC 2')) {
      console.log('[soaService.tsx] SOC 2 module is enabled')
      // SOC 2 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('SOC2Controls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const soc2Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] SOC 2 Controls:', soc2Controls);
              frameworksData.push({
                id: 'soc2',
                name: 'SOC 2',
                description: 'System and Organization Controls',
                controlCount: soc2Controls
              });
            }
          })
      );
    }

    if (isModuleEnabled('ISM')) {
      console.log('[soaService.tsx] ISM module is enabled')
      // ISM controls - fetch asynchronously
      promises.push(
        db
          .collection('customers')
          .doc(companyID)
          .collection('product')
          .doc('policyPackages')
          .collection('ISO27001keyDocuments')
          .doc('ismsSoa')
          .collection('ism')
          .get()
          .then((querySnapshot) => {
            const ismControls = querySnapshot.docs.length;
            console.log('[soaService.tsx] ISM Controls:', ismControls);
            frameworksData.push({
              id: 'ism',
              name: 'ISM',
              description: 'Information Security Manual',
              controlCount: ismControls
            });
            console.log('[soaService.tsx] Added ISM framework:', {
              id: 'ism',
              name: 'ISM',
              controlCount: ismControls
            });
          })
          .catch((error) => {
            console.error('[soaService.tsx] Error fetching ISM controls:', error);
            // Add framework anyway when enabled, even if controls fetch fails
            frameworksData.push({
              id: 'ism',
              name: 'ISM',
              description: 'Information Security Manual',
              controlCount: 0
            });
          })
      );
    }

    if (isModuleEnabled('privacyAct') || isModuleEnabled('pa')) {
      console.log('[soaService.tsx] Privacy Act module is enabled')
      // Privacy Act - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('paControls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const privacyControls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] Privacy Act Controls:', privacyControls);
              frameworksData.push({
                id: 'privacyAct',
                name: 'Privacy Act',
                description: 'Privacy Act Compliance',
                controlCount: privacyControls
              });
            }
          })
      );
    }

    if (isModuleEnabled('essential8') || isModuleEnabled('e8')) {
      console.log('[soaService.tsx] Essential 8 module is enabled')
      // Essential 8 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('essential8Controls')
          .get()
          .then((doc) => {
            const essential8Controls = doc.exists ? Object.keys(doc.data() || {}).length : 0;
            console.log('[soaService.tsx] Essential 8 Controls:', essential8Controls);
            frameworksData.push({
              id: 'essential8',
              name: 'Essential 8',
              description: 'Essential 8 Maturity Model',
              controlCount: essential8Controls
            });
            console.log('[soaService.tsx] Added Essential 8 framework:', {
              id: 'essential8',
              name: 'Essential 8',
              controlCount: essential8Controls
            });
          })
          .catch((error) => {
            console.error('[soaService.tsx] Error fetching Essential 8 controls:', error);
            // Add framework anyway when enabled, even if controls fetch fails
            frameworksData.push({
              id: 'essential8',
              name: 'Essential 8',
              description: 'Essential 8 Maturity Model',
              controlCount: 0
            });
          })
      );
    }

    if (isModuleEnabled('DISP') || isModuleEnabled('disp')) {
      console.log('[soaService.tsx] DISP module is enabled')
      // DISP - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('DISPControls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const dispControls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] DISP Controls:', dispControls);
              frameworksData.push({
                id: 'disp',
                name: 'DISP',
                description: 'Defence Industry Security Program',
                controlCount: dispControls
              });
            }
          })
      );
    }

    if (isModuleEnabled('SMB 1001') || isModuleEnabled('smb1001')) {
      console.log('[soaService.tsx] SMB 1001 module is enabled')
      // SMB 1001 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('SMB1001Controls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const smbControls = Object.keys(doc.data().Controls || {}).length;
              console.log('[soaService.tsx] SMB 1001 Controls:', smbControls);
              frameworksData.push({
                id: 'smb1001',
                name: 'SMB 1001',
                description: 'Small Business Security Standard',
                controlCount: smbControls
              });
            }
          })
      );
    }

    if (isModuleEnabled('ISO 42001') || isModuleEnabled('iso42001')) {
      console.log('[soaService.tsx] ISO 42001 module is enabled')
      // ISO 42001 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('iso420012023Controls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const iso42001Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] ISO 42001 Controls:', iso42001Controls);
              frameworksData.push({
                id: 'iso42001',
                name: 'ISO 42001',
                description: 'Information Security Management System',
                controlCount: iso42001Controls
              });
            }
          })
      );
    }

    if (isModuleEnabled('ISO 27701') || isModuleEnabled('iso27701')) {
      console.log('[soaService.tsx] ISO 27701 module is enabled')
      // ISO 27701 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('iso277012019Controls')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const iso27701Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] ISO 27701 Controls:', iso27701Controls);
              frameworksData.push({
                id: 'iso27701',
                name: 'ISO 27701',
                description: 'Information Security Management System',
                controlCount: iso27701Controls
              });
            }
          })
      );
    }

    // NIST 800-53 rev5
    if (isModuleEnabled('NIST 800-53') || isModuleEnabled('nist80053')) {
      console.log('[soaService.tsx] NIST 800-53 module is enabled')
      // NIST 800-53 rev5 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('nist800-53-rev5')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const nist80053rev5Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] NIST 800-53 rev5 Controls:', nist80053rev5Controls);
              frameworksData.push({
                id: 'nist800-53',
                name: 'NIST 800-53',
                description: 'National Institute of Standards and Technology Special Publication 800-53 Revision 5',
                controlCount: nist80053rev5Controls
              });
            }
          })
      );
    }

    // NIST CSF 2.0
    if (isModuleEnabled('NIST CSF') || isModuleEnabled('nistcsf')) {
      console.log('[soaService.tsx] NIST CSF module is enabled')
      // NIST CSF 2.0 - fetch asynchronously
      promises.push(
        db
          .collection('compliance')
          .doc('nist-csf-rev-2.0')
          .get()
          .then((doc) => {
            if (doc.exists) {
              const nistCsf20Controls = Object.keys(doc.data() || {}).length;
              console.log('[soaService.tsx] NIST CSF 2.0 Controls:', nistCsf20Controls);
              frameworksData.push({
                id: 'nist-csf',
                name: 'NIST CSF',
                description: 'National Institute of Standards and Technology Cybersecurity Framework Revision 2',
                controlCount: nistCsf20Controls
              });
            }
          })
      );
    }

    // Wait for all promises to complete
    await Promise.all(promises);

    return frameworksData;
  } catch (error) {
    console.error('Error fetching frameworks data:', error);
    throw error;
  }
}

/*
 * Fetches QGOV IS18 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @returns Promise containing an array of QGOV IS18 controls
 */
const fetchQGOVIS18Controls = async (companyID: string, soaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const controlIds = new Set<string>();
    const businessSponsorName = await getBusinessSponsorName();
    
    // Process controls from SOA document first
    Object.entries(soaData).forEach(([key, value]) => {
      // Skip non-control entries
      if (key === 'soaHistory' || key === 'version' || key === 'newSOA') return;

      const controlData = value as any;
      if (controlData.controlID && controlData.framework === 'qgov-is18') {
        controlsData.push({
          id: controlData.controlID,
          controlID: controlData.controlID,
          name: controlData.controlName || '',
          description: controlData.controlDesc || '',
          frameworkId: 'qgov-is18',
          framework: 'QGOV IS18',
          frameworkRaw: controlData.framework || 'QGOV IS18',
          applicable: controlData.controlApplicable === true,
          justification: controlData.controlJustification || '',
          status: controlData.implementationStatus || (controlData.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
          owner: controlData.owner || businessSponsorName
        });
        controlIds.add(controlData.controlID);
      }
    });

    return controlsData;
  } catch (error) {
    console.error('Error fetching QGOV IS18 controls:', error);
    throw error;
  }
};

/**
 * Fetches ISO 27001 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param soaData The SOA document data (optional)
 * @returns Promise containing an array of ISO 27001 controls
 */
const fetchISO27001Controls = async (companyID: string, soaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const controlIds = new Set<string>();
    const businessSponsorName = await getBusinessSponsorName();
    // Build a lookup map for controlJustification from soaData
    const soaJustificationMap: Record<string, string> = {};
    const controlApplicableMap: Record<string, boolean> = {};
    console.log('[soaService.tsx] SOA data:', soaData);
    // First process all controls from SOA data
    Object.entries(soaData).forEach(([key, value]) => {
      if (key === 'soaHistory' || key === 'version' || key === 'newSOA') return;
      const controlData = value as any;
      if (controlData.controlID && (controlData.framework === 'iso27001' || controlData.framework === 'iso270012022AnnexAControls' || !controlData.framework)) {
        // Always set the justification, even if empty string
        soaJustificationMap[controlData.controlID] = controlData.controlJustification ?? '';
        controlApplicableMap[controlData.controlID] = controlData.controlApplicable === undefined || controlData.controlApplicable === true || controlData.controlApplicable === 'true';
        
        // If explicitly marked as not applicable, set status accordingly
        let status = controlData.implementationStatus;
        if (!status) {
          if (controlData.implementationStatus?.toLowerCase() === 'not implemented' || controlData.implementationStatus?.toLowerCase() === 'unknown') {
            status = 'Not implemented';
          } else if (controlData.controlApplicable === 'not applicable' || controlData.controlApplicable === false || controlData.controlApplicable === 'false'|| controlData.implementationStatus?.toLowerCase() === 'not applicable') {
            status = 'Not applicable';
          } else {
            status = 'Fully implemented';
          }
        }
        
        console.log(`[soaService.tsx] Found SOA control: ${controlData.controlID} - Status: ${status}, Applicable: ${controlApplicableMap[controlData.controlID]}`);
        
        controlsData.push({
          id: controlData.controlID,
          controlID: controlData.controlID,
          name: controlData.controlName || '',
          description: controlData.controlDesc || '',
          frameworkId: 'iso27001',
          framework: 'ISO 27001',
          frameworkRaw: controlData.framework || 'ISO 27001',
          applicable: controlApplicableMap[controlData.controlID],
          justification: controlData.controlJustification || '',
          status: status,
          owner: controlData.owner || businessSponsorName
        });
        controlIds.add(controlData.controlID);
      }
    });

    try {
      // Fetch controls from compliance/iso270012022AnnexAControls
      const complianceDoc = await db
        .collection('compliance')
        .doc('iso270012022AnnexAControls')
        .get();

      const complianceData = complianceDoc.data() || {};

      // Process each control in the document
      Object.values(complianceData).forEach((controlData: any) => {
        if (controlData && controlData.controlID && !controlIds.has(controlData.controlID)) {
          // For compliance controls not in SOA, check their status in SOA data if they exist
          const soaControl = soaData[controlData.controlID];
          let status: 'Fully implemented' | 'Not implemented' | 'Partially implemented' | 'Not applicable' | 'Unknown' = 'Fully implemented';
          let isApplicable = true;
          
          if (soaControl) {
            // If control exists in SOA data, use its status
            if (soaControl.implementationStatus) {
              status = soaControl.implementationStatus as any;
            } else if (soaControl.controlApplicable === false || soaControl.controlApplicable === 'false') {
              status = 'Not implemented';
            } else if (soaControl.controlApplicable === 'not applicable') {
              status = 'Not applicable';
            }
            isApplicable = soaControl.controlApplicable !== false && 
                          soaControl.controlApplicable !== 'false' &&
                          soaControl.implementationStatus !== 'not applicable';
          }
          
          // Use justification from SOA if available, otherwise from compliance data
          const justification = soaJustificationMap[controlData.controlID] || 
                              controlData.controlJustification || 
                              controlData.description || '';
          
          console.log(`[soaService.tsx] Found compliance control: ${controlData.controlID} - Status: ${status}, Applicable: ${isApplicable}`);
          
          controlsData.push({
            id: controlData.controlID,
            controlID: controlData.controlID,
            name: controlData.controlName || controlData.title || '',
            description: controlData.controlDesc || controlData.description || '',
            frameworkId: 'iso27001',
            framework: 'ISO 27001',
            frameworkRaw: 'ISO 27001',
            applicable: isApplicable,
            justification,
            status: status,
            owner: businessSponsorName
          });
        }
      });
    } catch (error) {
      console.error('Error fetching compliance controls:', error);
      // Continue with just the SOA controls if there's an error
    }

    return controlsData;
  } catch (error) {
    console.error('Error in fetchISO27001Controls:', error);
    return [];
  }
};

/**
 * Fetches ISM controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @returns Promise containing an array of ISM controls
 */
// Helper to check if a value is truthy for RFFR (os/s fields)
const isRFFRTruthy = (value: any): boolean => {
  if (value === true) return true;
  if (typeof value === 'string') {
    const lower = value.toLowerCase().trim();
    return lower === 'yes' || lower === 'y' || lower === 'true';
  }
  return false;
};

const fetchISMControls = async (companyID: string): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();
    const seenControlIds = new Set<string>();

    // Path 1: Get ISM controls from customer-specific location
    // customers/{companyID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism
    try {
      const ismControls = await db.collection('customers').doc(companyID).collection('product').doc('policyPackages').collection('ISO27001keyDocuments').doc('ismsSoa').collection('ism').get();

      if (!ismControls.empty) {
        ismControls.docs.forEach((doc) => {
          const ismData = doc.data();
          seenControlIds.add(doc.id);
          // Check if this is an RFFR control (os or s field is truthy)
          const isRFFR = isRFFRTruthy(ismData.os) || isRFFRTruthy(ismData.s);
          controlsData.push({
            id: doc.id,
            controlID: doc.id,
            name: ismData.controlDescription || ismData.description || '',
            description: ismData.controlDescription || ismData.description || '',
            frameworkId: 'ism',
            framework: 'ism',
            frameworkRaw: 'ism',
            applicable: ismData.os === 'yes' || ismData.o === 'yes',
            justification: ismData.implementationDetails || '',
            status: ismData.implementationStatus || 'Unknown',
            owner: ismData.owner || businessSponsorName,
            isRFFR
          });
        });
        console.log(`[soaService.tsx] Found ${ismControls.docs.length} ISM controls from customer path`);
      }
    } catch (err) {
      console.warn('[soaService.tsx] Error fetching ISM controls from customer path:', err);
    }

    // Path 2: Get ISM controls from global compliance location
    // compliance/ism/controls/*
    try {
      const globalIsmControls = await db.collection('compliance').doc('ism').collection('controls').get();

      if (!globalIsmControls.empty) {
        globalIsmControls.docs.forEach((doc) => {
          // Skip if we already have this control from the customer path
          if (seenControlIds.has(doc.id)) return;
          
          const ismData = doc.data();
          seenControlIds.add(doc.id);
          // Check if this is an RFFR control (os or s field is truthy)
          const isRFFR = isRFFRTruthy(ismData.os) || isRFFRTruthy(ismData.s);
          controlsData.push({
            id: doc.id,
            controlID: doc.id,
            name: ismData.controlDescription || ismData.description || ismData.name || '',
            description: ismData.controlDescription || ismData.description || '',
            frameworkId: 'ism',
            framework: 'ism',
            frameworkRaw: 'ism',
            applicable: ismData.os === 'yes' || ismData.o === 'yes' || ismData.applicable === true,
            justification: ismData.implementationDetails || ismData.justification || '',
            status: ismData.implementationStatus || ismData.status || 'Unknown',
            owner: ismData.owner || businessSponsorName,
            isRFFR
          });
        });
        console.log(`[soaService.tsx] Found ${globalIsmControls.docs.length} ISM controls from global compliance path`);
      }
    } catch (err) {
      console.warn('[soaService.tsx] Error fetching ISM controls from global compliance path:', err);
    }

    const rffrCount = controlsData.filter(c => c.isRFFR).length;
    console.log(`[soaService.tsx] Total ISM controls: ${controlsData.length} (${rffrCount} RFFR)`);
    return controlsData;
  } catch (error) {
    console.error('Error fetching ISM controls:', error);
    return [];
  }
};

/**
 * Fetches SOC2 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of SOC2 controls
 */
const fetchSOC2Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();

    // Get SOC2 controls
    const soc2Controls = await db.collection('compliance').doc('SOC2Controls').get();

    if (soc2Controls.exists) {
      const soc2ControlsData = soc2Controls.data() || {};

      // Process SOC2 controls
      Object.keys(soc2ControlsData).forEach(key => {
        const globalControl = soc2ControlsData[key];

        if (globalControl?.controlID) {
          // Try to get customer-specific data for this control if it exists
          const customerControl = customerSoaData[key] || {};

          controlsData.push({
            id: globalControl.controlID,
            controlID: globalControl.controlID,
            name: globalControl.controlName || '',
            description: globalControl.controlDescription || '',
            frameworkId: 'soc2',
            framework: 'SOC2',
            frameworkRaw: 'SOC2',
            applicable: customerControl.controlApplicable === true,
            justification: customerControl.controlJustification || globalControl.controlJustification || '',
            status: customerControl.implementationStatus ||
              (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
            owner: customerControl.owner || businessSponsorName
          });
        }
      });
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching SOC2 controls:', error);
    return [];
  }
};

/**
 * Fetches DISP controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of DISP controls
 */
const fetchDISPControls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();
    const controlIds = new Set<string>();

    console.log('[soaService.tsx] Starting DISP controls processing...');

    // Build maps for customer SOA data
    const justificationMap: Record<string, string> = {};
    const applicabilityMap: Record<string, boolean> = {};
    const implementationStatusMap: Record<string, string> = {};
    const ownerMap: Record<string, string> = {};

    // Process customer SOA data for DISP controls
    if (customerSoaData) {
      Object.keys(customerSoaData).forEach(key => {
        const controlData = customerSoaData[key];
        if (controlData.controlID && (controlData.framework === 'disp' || controlData.framework === 'DISP' || controlData.framework === 'DISPControls')) {
          console.log('[soaService.tsx] Processing DISP control from SOA:', key, controlData);
          
          justificationMap[controlData.controlID] = controlData.controlJustification || '';
          applicabilityMap[controlData.controlID] = controlData.controlApplicable !== false;
          implementationStatusMap[controlData.controlID] = controlData.implementationStatus || '';
          ownerMap[controlData.controlID] = controlData.owner || businessSponsorName;
          
          // Add control from SOA data
          const status = controlData.implementationStatus;
          let finalStatus: 'Fully implemented' | 'Partially implemented' | 'Not implemented' | 'Not applicable' | 'Unknown' = 'Not implemented';
          
          if (status) {
            finalStatus = status as 'Fully implemented' | 'Partially implemented' | 'Not implemented' | 'Not applicable' | 'Unknown';
          } else {
            if (controlData.controlApplicable === false || controlData.controlApplicable === 'false') {
              finalStatus = 'Not applicable';
            } else {
              finalStatus = 'Fully implemented'; // Default to applicable if not explicitly set
            }
          }

          controlsData.push({
            id: controlData.controlID,
            controlID: controlData.controlID,
            name: controlData.controlName || '',
            description: controlData.controlDescription || controlData.controlDesc || '',
            frameworkId: 'disp',
            framework: 'DISP',
            frameworkRaw: 'DISP',
            applicable: controlData.controlApplicable !== false,
            justification: controlData.controlJustification || '',
            status: finalStatus,
            owner: controlData.owner || businessSponsorName
          });
          controlIds.add(controlData.controlID);
        }
      });
    }

    // Get compliance modules doc to check DISP configuration
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    console.log('[soaService.tsx] Compliance modules doc:', complianceModulesDoc.data());
    if (complianceModulesDoc.exists && (complianceModulesDoc.data()?.disp?.enabled || complianceModulesDoc.data()?.disp?.Enabled || complianceModulesDoc.data()?.disp?.Enabled === "true" || complianceModulesDoc.data()?.disp?.enabled === "true" || complianceModulesDoc.data()?.disp?.active || complianceModulesDoc.data()?.disp?.active === "true")) {
      // Get DISP settings
      const dispSettings = complianceModulesDoc.data().disp;

      // Get global DISP controls definitions
      const dispControls = await db.collection('compliance').doc('DISPControls').get();

      if (dispControls.exists) {
        const dispControlsData = dispControls.data() || {};

        // Process each global control
        Object.keys(dispControlsData).forEach(key => {
          const globalControl = dispControlsData[key];
          
          // Skip if already processed from SOA data
          if (controlIds.has(globalControl.controlID || key)) {
            return;
          }

          // Apply DISP filtering based on category and level
          if (globalControl.DISPCategory) {
            let shouldInclude = false;
            const category = globalControl.DISPCategory;
            const level = globalControl.DISPLevel;

            // Dynamically find the DISP setting value for this category
            // dispSettings may be an object with numeric keys, each containing { settingName, value }
            let dispSettingValue = undefined;
            if (typeof dispSettings === 'object') {
              // Search for a setting with a matching settingName
              const settingEntry = Object.values(dispSettings).find((entry) => {
                if (!entry || typeof entry !== 'object') return false;
                const e = entry as { settingName?: string; value?: string };
                // Normalize both strings for comparison
                return (
                  e.settingName &&
                  e.settingName.replace(/\s+/g, '').toLowerCase().includes(
                    ('DISP ' + category).replace(/\s+/g, '').toLowerCase()
                  )
                );
              }) as { settingName?: string; value?: string } | undefined;
              if (settingEntry && settingEntry.value) {
                dispSettingValue = settingEntry.value;
              }
            }
            if (dispSettingValue) {
              shouldInclude = checkDISPLevel(dispSettingValue, level);
            }

            if (shouldInclude) {
              const controlID = globalControl.controlID || key;
              const customerControl = customerSoaData[controlID] || {};
              
              // Use customer data if available, otherwise use global data
              const applicable = customerControl.controlApplicable !== false;
              const justification = customerControl.controlJustification || globalControl.controlDescription || '';
              const owner = customerControl.owner || businessSponsorName;
              
              let status: 'Fully implemented' | 'Partially implemented' | 'Not implemented' | 'Not applicable' | 'Unknown' = 'Not implemented';
              if (customerControl.implementationStatus) {
                status = customerControl.implementationStatus as 'Fully implemented' | 'Partially implemented' | 'Not implemented' | 'Not applicable' | 'Unknown';
              } else {
                if (customerControl.controlApplicable === false || customerControl.controlApplicable === 'false') {
                  status = 'Not applicable';
                } else {
                  status = 'Fully implemented'; // Default to applicable if not explicitly set
                }
              }

              // Name/Description logic: prefer controlName; otherwise use controlDescription/controlDesc for name, and leave description blank. If both present, use controlName for name and controlDescription/controlDesc for description.
              let name = '';
              let description = '';
              if (globalControl.controlName) {
                name = globalControl.controlName;
                description = globalControl.controlDescription || globalControl.controlDesc || '';
              } else if (globalControl.controlDescription) {
                name = globalControl.controlDescription;
                description = '';
              } else if (globalControl.controlDesc) {
                name = globalControl.controlDesc;
                description = '';
              }
              controlsData.push({
                id: controlID,
                controlID: controlID,
                name,
                description,
                frameworkId: 'disp',
                framework: 'DISP',
                frameworkRaw: 'DISP',
                applicable: applicable,
                justification: justification,
                status: status,
                owner: owner
              });
              controlIds.add(controlID);
            }
          }
        });
      }
    }

    console.log('[soaService.tsx] DISP controls processed:', controlsData.length);
    return controlsData;
  } catch (error) {
    console.error('Error fetching DISP controls:', error);
    return [];
  }
};

// Helper function to check DISP levels
const checkDISPLevel = (customerLevel: string, controlLevel: string): boolean => {
  if (customerLevel === 'entryLevel') {
    return controlLevel === 'entryLevel';
  } else if (customerLevel === 'levelOne') {
    return controlLevel === 'entryLevel' || controlLevel === 'levelOne';
  } else if (customerLevel === 'levelTwo') {
    return controlLevel === 'entryLevel' || controlLevel === 'levelOne' || controlLevel === 'levelTwo';
  } else if (customerLevel === 'levelThree') {
    return controlLevel === 'entryLevel' || controlLevel === 'levelOne' ||
      controlLevel === 'levelTwo' || controlLevel === 'levelThree';
  }
  return false;
};

/**
 * Fetches SMB1001 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of SMB1001 controls
 */
const fetchSMB1001Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();

    // Check if the customer has SMB1001 enabled and get the level
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    // Support SMB1001/smb1001, Enabled/enabled, Level/level (in that order)
    const modules = complianceModulesDoc.data() || {};
    const smbKey = modules.SMB1001 ? 'SMB1001' : (modules.smb1001 ? 'smb1001' : null);
    const smbModule = smbKey ? modules[smbKey] : null;
    const enabled = smbModule ? (smbModule.Enabled ?? smbModule.enabled) : undefined;
    const level = smbModule ? (smbModule.Level ?? smbModule.level) : undefined;
    if (complianceModulesDoc.exists && smbModule && enabled && level) {

      const smbLevel = level;

      // Get global SMB1001 controls
      const smb1001Controls = await db.collection('compliance').doc('SMB1001Controls').get();

      if (smb1001Controls.exists && smb1001Controls.data()?.Controls) {
        const smbControlsData = smb1001Controls.data().Controls;

        console.log('[soaService.tsx] SMB1001 controls:', smbControlsData)
        // Process SMB1001 controls for the customer's level
        Object.keys(smbControlsData).forEach(key => {
          const globalControl = smbControlsData[key];
          const controlID = globalControl.controlID;

          // Only include controls for the customer's selected level
          if (globalControl.level === smbLevel) {
            // Try to get customer-specific data for this control if it exists
            const customerControl = customerSoaData[controlID] || globalControl || {};

            // If explicitly marked as not applicable, set status accordingly
            let status = customerControl.implementationStatus || customerControl.controlApplicable;
            if (status) {
              // Check if boolean first 
              if (typeof status === 'boolean') {
                if (status) {
                  status = 'Fully implemented';
                } else {
                  status = 'Not implemented';
                }
              } else {
                if (customerControl.controlApplicable && !customerControl.implementationStatus) {
                  if (customerControl.controlApplicable === 'true' || customerControl.controlApplicable === true) {
                    status = 'Fully implemented';
                  } else {
                    status = 'Not implemented';
                  }
                } else if (customerControl.implementationStatus?.toLowerCase() === 'not implemented' || customerControl.implementationStatus?.toLowerCase() === 'unknown') {
                  status = 'Not implemented';
                } else if (customerControl.controlApplicable === 'not applicable' || customerControl.controlApplicable === false || customerControl.controlApplicable === 'false'|| customerControl.implementationStatus?.toLowerCase() === 'not applicable') {
                  status = 'Not applicable';
                } else {
                  status = 'Fully implemented';
                }
              }
            } else {
              status = 'Fully implemented';
            }

            controlsData.push({
              id: globalControl.controlID,
              controlID: globalControl.controlID,
              name: globalControl.controlName || '',
              description: globalControl.controlDescription || '',
              frameworkId: 'smb1001',
              framework: 'SMB1001',
              frameworkRaw: 'SMB1001',
              applicable: customerControl.controlApplicable === true || customerControl.controlApplicable === 'true' || globalControl.implemented === true || true,
              justification: customerControl.controlJustification || globalControl.controlDescription || '',
              status: status,
              owner: customerControl.owner || businessSponsorName
            });
          }
        });
      }
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching SMB1001 controls:', error);
    return [];
  }
};

/**
 * Fetches Privacy Act controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of Privacy Act controls
 */
const fetchPrivacyActControls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const controlIds = new Set<string>();
    const businessSponsorName = await getBusinessSponsorName();

    // Build a lookup map for controlJustification from soaData
    const soaJustificationMap: Record<string, string> = {};
    const controlApplicableMap: Record<string, boolean> = {};
    
    // Check if Privacy Act is enabled
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    const moduleData = complianceModulesDoc.data();
    const isEnabled = (
      moduleData?.privacyAct?.Enabled === true ||
      moduleData?.privacyAct?.enabled === true ||
      moduleData?.privacyAct?.Active === true ||
      moduleData?.privacyAct?.active === true ||
    
      moduleData?.pa?.Enabled === true ||
      moduleData?.pa?.enabled === true ||
      moduleData?.pa?.Active === true ||
      moduleData?.pa?.active === true ||
    
      (moduleData?.privacyAct && moduleData.privacyAct !== false) ||
      (moduleData?.pa && moduleData.pa !== false)
    );

    console.log('[soaService.tsx] Privacy Act module check:', { isEnabled, moduleData });

    if (complianceModulesDoc.exists && isEnabled) {

      // First process all controls from SOA data
      Object.entries(customerSoaData).forEach(([key, value]) => {
        if (key === 'soaHistory' || key === 'version' || key === 'newSOA') return;
        const controlData = value as any;
        if (controlData.controlID && (controlData.framework === 'privacyAct' || controlData.framework === 'paControls' || controlData.framework === 'Privacy Act')) {
          // Always set the justification, even if empty string
          soaJustificationMap[controlData.controlID] = controlData.controlJustification ?? '';
          controlApplicableMap[controlData.controlID] = controlData.controlApplicable === undefined || controlData.controlApplicable === true || controlData.controlApplicable === 'true';
          
          // If explicitly marked as not applicable, set status accordingly
          let status = controlData.implementationStatus;
          if (!status) {
            if (controlData.implementationStatus?.toLowerCase() === 'not implemented' || controlData.implementationStatus?.toLowerCase() === 'unknown') {
              status = 'Not implemented';
            } else if (controlData.controlApplicable === 'not applicable' || controlData.controlApplicable === false || controlData.controlApplicable === 'false'|| controlData.implementationStatus?.toLowerCase() === 'not applicable') {
              status = 'Not applicable';
            } else {
              status = 'Fully implemented';
            }
          }
          
          console.log(`[soaService.tsx] Found SOA Privacy Act control: ${controlData.controlID} - Status: ${status}, Applicable: ${controlApplicableMap[controlData.controlID]}`);
          
          controlsData.push({
            id: controlData.controlID,
            controlID: controlData.controlID,
            name: controlData.controlName || '',
            description: controlData.controlDesc || '',
            frameworkId: 'privacyAct',
            framework: 'Privacy Act',
            frameworkRaw: controlData.framework || 'Privacy Act',
            applicable: controlApplicableMap[controlData.controlID],
            justification: controlData.controlJustification || '',
            status: status,
            owner: controlData.owner || businessSponsorName
          });
          controlIds.add(controlData.controlID);
        }
      });

      try {
        // Fetch controls from compliance/paControls
        const complianceDoc = await db
          .collection('compliance')
          .doc('paControls')
          .get();

        const complianceData = complianceDoc.data() || {};

        // Process each control in the document
        Object.values(complianceData).forEach((controlData: any) => {
          if (controlData && controlData.controlID && !controlIds.has(controlData.controlID)) {
            // For compliance controls not in SOA, check their status in SOA data if they exist
            const soaControl = customerSoaData[controlData.controlID];
            let status: 'Fully implemented' | 'Not implemented' | 'Partially implemented' | 'Not applicable' | 'Unknown' = 'Fully implemented';
            let isApplicable = true;
            
            if (soaControl) {
              // If control exists in SOA data, use its status
              if (soaControl.implementationStatus) {
                status = soaControl.implementationStatus as any;
              } else if (soaControl.controlApplicable === false || soaControl.controlApplicable === 'false') {
                status = 'Not implemented';
              } else if (soaControl.controlApplicable === 'not applicable') {
                status = 'Not applicable';
              }
              isApplicable = soaControl.controlApplicable !== false && 
                            soaControl.controlApplicable !== 'false' &&
                            soaControl.implementationStatus !== 'not applicable';
            }
            
            // Use justification from SOA if available, otherwise from compliance data
            const justification = soaJustificationMap[controlData.controlID] || 
                                controlData.controlJustification || 
                                controlData.description || '';
            
            console.log(`[soaService.tsx] Found compliance Privacy Act control: ${controlData.controlID} - Status: ${status}, Applicable: ${isApplicable}`);
            
            controlsData.push({
              id: controlData.controlID,
              controlID: controlData.controlID,
              name: controlData.controlName || controlData.title || '',
              description: controlData.controlDesc || controlData.description || '',
              frameworkId: 'privacyAct',
              framework: 'Privacy Act',
              frameworkRaw: 'Privacy Act',
              applicable: isApplicable,
              justification,
              status: status,
              owner: businessSponsorName
            });
          }
        });
      } catch (error) {
        console.error('Error fetching compliance Privacy Act controls:', error);
        // Continue with just the SOA controls if there's an error
      }
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching Privacy Act controls:', error);
    return [];
  }
};

/**
 * Fetches Essential 8 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of Essential 8 controls
 */
const fetchEssential8Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const controlIds = new Set<string>();
    const businessSponsorName = await getBusinessSponsorName();

    // Build a lookup map for controlJustification from soaData
    const soaJustificationMap: Record<string, string> = {};
    const controlApplicableMap: Record<string, boolean> = {};

    // Check if Essential 8 is enabled
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    const moduleData = complianceModulesDoc.data();
    const isEnabled = (
      moduleData?.essential8?.Enabled === true ||
      moduleData?.essential8?.enabled === true ||
      moduleData?.essential8?.Active === true ||
      moduleData?.essential8?.active === true ||
    
      moduleData?.essential8Enabled === true ||
      moduleData?.essential8Active === true ||
    
      (moduleData?.essential8 && moduleData.essential8 !== false)
    );

    // Get the Essential 8 enabled levels (e.g., ["e8_level1", "e8_level2"])
    const enabledLevels = moduleData?.essential8?.levels || [];
    
    // Extract numeric levels from the array (e.g., ["e8_level1", "e8_level2"] -> [1, 2])
    const enabledLevelNumbers = enabledLevels
      .map((level: string) => {
        // Match "level" followed by digits (e.g., "level1" -> "1")
        const match = level.match(/level(\d+)/i);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((num: number | null) => num !== null);
    
    // If no levels specified, default to level 1
    const targetLevels = enabledLevelNumbers.length > 0 ? enabledLevelNumbers : [1];

    console.log('[soaService.tsx] Essential 8 module check:', { 
      isEnabled, 
      enabledLevels, 
      targetLevels, 
      moduleData 
    });

    // Helper function to check if control should be included based on level coverage
    const shouldIncludeControl = (e8LevelCoverage: any[]): boolean => {
      console.log('[soaService.tsx] Checking control level coverage:', { e8LevelCoverage, targetLevels });
      if (!e8LevelCoverage || !Array.isArray(e8LevelCoverage)) {
        return true; // Include if no level coverage specified
      }
      // Include if any of the control's levels match the enabled levels
      return e8LevelCoverage.some(level => {
        const levelNum = parseInt(level.toString(), 10);
        return !isNaN(levelNum) && targetLevels.includes(levelNum);
      });
    };

    if (complianceModulesDoc.exists && isEnabled) {

      // First, fetch the E8Controls to get level coverage data
      let e8ControlsLevelMap: Record<string, any[]> = {};
      try {
        const complianceDoc = await db
          .collection('compliance')
          .doc('E8Controls')
          .get();

        const complianceData = complianceDoc.data() || {};
        
        // Build a map of controlID -> e8LevelCoverage
        Object.values(complianceData).forEach((controlData: any) => {
          if (controlData?.controlID && controlData?.e8LevelCoverage) {
            e8ControlsLevelMap[controlData.controlID] = controlData.e8LevelCoverage;
          }
        });
        
        console.log('[soaService.tsx] Built E8 level coverage map:', e8ControlsLevelMap);
      } catch (error) {
        console.error('[soaService.tsx] Error fetching E8Controls for level mapping:', error);
      }

      // First process all controls from SOA data
      Object.entries(customerSoaData).forEach(([key, value]) => {
        if (key === 'soaHistory' || key === 'version' || key === 'newSOA') return;
        const controlData = value as any;
        if (controlData.controlID && (controlData.framework === 'essential8' || controlData.framework === 'E8Controls' || controlData.framework === 'Essential 8')) {
          // Get level coverage from E8Controls map (SOA data doesn't have this field)
          const levelCoverage = e8ControlsLevelMap[controlData.controlID] || controlData.e8LevelCoverage;
          
          // Check if control should be included based on level coverage
          if (!shouldIncludeControl(levelCoverage)) {
            console.log(`[soaService.tsx] Skipping SOA E8 control ${controlData.controlID} - level coverage ${JSON.stringify(levelCoverage)} not in enabled levels ${JSON.stringify(targetLevels)}`);
            return;
          }
          // Always set the justification, even if empty string
          soaJustificationMap[controlData.controlID] = controlData.controlJustification ?? '';
          controlApplicableMap[controlData.controlID] = controlData.controlApplicable === undefined || controlData.controlApplicable === true || controlData.controlApplicable === 'true';
          
          // If explicitly marked as not applicable, set status accordingly
          let status = controlData.implementationStatus;
          if (!status) {
            if (controlData.implementationStatus?.toLowerCase() === 'not implemented' || controlData.implementationStatus?.toLowerCase() === 'unknown') {
              status = 'Not implemented';
            } else if (controlData.controlApplicable === 'not applicable' || controlData.controlApplicable === false || controlData.controlApplicable === 'false'|| controlData.implementationStatus?.toLowerCase() === 'not applicable') {
              status = 'Not applicable';
            } else {
              status = 'Fully implemented';
            }
          }
          
          console.log(`[soaService.tsx] Found SOA Essential 8 control: ${controlData.controlID} - Status: ${status}, Applicable: ${controlApplicableMap[controlData.controlID]}`);
          
          controlsData.push({
            id: controlData.controlID,
            controlID: controlData.controlID,
            name: controlData.controlName || '',
            description: controlData.controlDesc || '',
            frameworkId: 'essential8',
            framework: 'Essential 8',
            frameworkRaw: controlData.framework || 'Essential 8',
            applicable: controlApplicableMap[controlData.controlID],
            justification: controlData.controlJustification || '',
            status: status,
            owner: controlData.owner || businessSponsorName,
            levelCoverage: levelCoverage
          });
          controlIds.add(controlData.controlID);
        }
      });

      try {
        // Fetch controls from compliance/essential8Controls
        const complianceDoc = await db
          .collection('compliance')
          .doc('E8Controls')
          .get();

        const complianceData = complianceDoc.data() || {};

        // Process each control in the document
        Object.values(complianceData).forEach((controlData: any) => {
          if (controlData && controlData.controlID && !controlIds.has(controlData.controlID)) {
            // Check if control should be included based on level coverage
            if (!shouldIncludeControl(controlData.e8LevelCoverage)) {
              console.log(`[soaService.tsx] Skipping E8 control ${controlData.controlID} - level coverage ${JSON.stringify(controlData.e8LevelCoverage)} not in enabled levels ${JSON.stringify(targetLevels)}`);
              return;
            }
            // For compliance controls not in SOA, check their status in SOA data if they exist
            const soaControl = customerSoaData[controlData.controlID];
            let status: 'Fully implemented' | 'Not implemented' | 'Partially implemented' | 'Not applicable' | 'Unknown' = 'Fully implemented';
            let isApplicable = true;
            
            if (soaControl) {
              // If control exists in SOA data, use its status
              if (soaControl.implementationStatus) {
                status = soaControl.implementationStatus as any;
              } else if (soaControl.controlApplicable === false || soaControl.controlApplicable === 'false') {
                status = 'Not implemented';
              } else if (soaControl.controlApplicable === 'not applicable') {
                status = 'Not applicable';
              }
              isApplicable = soaControl.controlApplicable !== false && 
                            soaControl.controlApplicable !== 'false' &&
                            soaControl.implementationStatus !== 'not applicable';
            }
            
            // Use justification from SOA if available, otherwise from compliance data
            const justification = soaJustificationMap[controlData.controlID] || 
                                controlData.controlJustification || 
                                controlData.description || '';
            
            console.log(`[soaService.tsx] Found compliance Essential 8 control: ${controlData.controlID} - Status: ${status}, Applicable: ${isApplicable}`);
            
            controlsData.push({
              id: controlData.controlID,
              controlID: controlData.controlID,
              name: controlData.controlName || controlData.title || '',
              description: controlData.controlDesc || controlData.description || '',
              frameworkId: 'essential8',
              framework: 'Essential 8',
              frameworkRaw: 'Essential 8',
              applicable: isApplicable,
              justification,
              status: status,
              owner: businessSponsorName,
              levelCoverage: controlData.e8LevelCoverage
            });
          }
        });
      } catch (error) {
        console.error('Error fetching compliance Essential 8 controls:', error);
        // Continue with just the SOA controls if there's an error
      }
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching Essential 8 controls:', error);
    return [];
  }
};

// DEPRECATED
// /**
//  * Fetches NIST controls from Firestore
//  * @param companyID The company ID to fetch controls for
//  * @param customerSoaData The customer SOA document data
//  * @returns Promise containing an array of NIST controls
//  */
// const fetchNISTControls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
//   try {
//     const controlsData: Control[] = [];
//     const businessSponsorName = await getBusinessSponsorName();

//     // Get global NIST controls
//     const nistControls = await db.collection('compliance').doc('NISTControls').get();

//     if (nistControls.exists) {
//       const nistControlsData = nistControls.data() || {};

//       // Process NIST controls
//       Object.keys(nistControlsData).forEach(key => {
//         const globalControl = nistControlsData[key];
//         // Try to get customer-specific data for this control if it exists
//         const customerControl = customerSoaData[key] || {};

//         controlsData.push({
//           id: key,
//           controlID: globalControl.controlID || key,
//           name: globalControl.controlName || '',
//           description: globalControl.controlDescription || '',
//           frameworkId: 'nist',
//           framework: 'NIST',
//           frameworkRaw: 'NIST',
//           applicable: customerControl.controlApplicable === true,
//           justification: customerControl.controlJustification || globalControl.controlJustification || '',
//           status: customerControl.implementationStatus ||
//             (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
//           owner: customerControl.owner || businessSponsorName
//         });
//       });
//     }

//     return controlsData;
//   } catch (error) {
//     console.error('Error fetching NIST controls:', error);
//     return [];
//   }
// };

/**
 * Fetches ISO 27701 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of ISO 27701 controls
 */
const fetchISO27701Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();

    // Check if ISO 27701 is enabled
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    const moduleData = complianceModulesDoc.data();
    const isEnabled = moduleData?.iso27701?.Enabled === true || 
                     moduleData?.iso27701Enabled === true ||
                     (moduleData?.iso27701 && moduleData.iso27701 !== false);

    console.log('[soaService.tsx] ISO 27701 module check:', { isEnabled, moduleData });

    if (!complianceModulesDoc.exists || !isEnabled) {
      console.log('[soaService.tsx] ISO 27701 module not enabled, returning empty controls');
      return [];
    }

    // Get global ISO 27701 controls
    const iso27701Controls = await db.collection('compliance').doc('iso277012019Controls').get();

    if (iso27701Controls.exists) {
      const iso27701ControlsData = iso27701Controls.data() || {};

      // Process ISO 27701 controls
      Object.keys(iso27701ControlsData).forEach(key => {
        const globalControl = iso27701ControlsData[key];
        // Try to get customer-specific data for this control if it exists
        const customerControl = customerSoaData[key] || {};

        controlsData.push({
          id: key,
          controlID: globalControl.controlID || key,
          name: globalControl.controlName || '',
          description: globalControl.controlDescription || '',
          frameworkId: 'iso27701',
          framework: 'ISO 27701',
          frameworkRaw: 'ISO 27701',
          applicable: customerControl.controlApplicable === true,
          justification: customerControl.controlJustification || globalControl.controlJustification || '',
          status: customerControl.implementationStatus ||
            (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
          owner: customerControl.owner || businessSponsorName
        });
      });
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching ISO 27701 controls:', error);
    return [];
  }
};


/**
 * Fetches ISO 42001 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of ISO 42001 controls
 */
const fetchISO42001Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();

    // Check if ISO 27701 is enabled
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    const moduleData = complianceModulesDoc.data();
    const isEnabled = moduleData?.iso42001?.Enabled === true || 
                     moduleData?.iso42001Enabled === true ||
                     (moduleData?.iso42001 && moduleData.iso42001 !== false);

    console.log('[soaService.tsx] ISO 42001 module check:', { isEnabled, moduleData });

    if (!complianceModulesDoc.exists || !isEnabled) {
      console.log('[soaService.tsx] ISO 42001 module not enabled, returning empty controls');
      return [];
    }

    // Get global ISO 42001 controls
    const iso42001Controls = await db.collection('compliance').doc('iso420012023Controls').get();

    if (iso42001Controls.exists) {
      const iso42001ControlsData = iso42001Controls.data() || {};

      // Process ISO 42001 controls
      Object.keys(iso42001ControlsData).forEach(key => {
        const globalControl = iso42001ControlsData[key];
        // Try to get customer-specific data for this control if it exists
        const customerControl = customerSoaData[key] || {};

        controlsData.push({
          id: key,
          controlID: globalControl.controlID || key,
          name: globalControl.controlName || '',
          description: globalControl.controlDescription || '',
          frameworkId: 'iso42001',
          framework: 'ISO 42001',
          frameworkRaw: 'ISO 42001',
          applicable: customerControl.controlApplicable === true,
          justification: customerControl.controlJustification || globalControl.controlJustification || '',
          status: customerControl.implementationStatus ||
            (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
          owner: customerControl.owner || businessSponsorName
        });
      });
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching ISO 42001 controls:', error);
    return [];
  }
};

/**
 * Fetches NIST 800-53 controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of NIST 800-53 controls
 */
  const fetchNIST80053Controls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
    try {
      const controlsData: Control[] = [];
      const businessSponsorName = await getBusinessSponsorName();

      // Check if NIST 800-53 is enabled
      const complianceModulesDoc = await db.collection('customers')
        .doc(companyID)
        .collection('companySettings')
        .doc('complianceModules')
        .get();

      const moduleData = complianceModulesDoc.data();
      const isEnabled = moduleData?.nist80053?.Enabled === true || 
                      moduleData?.nist80053Enabled === true ||
                      (moduleData?.nist80053 && moduleData.nist80053 !== false);

      console.log('[soaService.tsx] NIST 800-53 module check:', { isEnabled, moduleData });

      if (!complianceModulesDoc.exists || !isEnabled) {
        console.log('[soaService.tsx] NIST 800-53 module not enabled, returning empty controls');
        return [];
      }

      // Get global NIST 800-53 controls
      const nist80053Controls = await db.collection('compliance').doc('nist800-53-rev5').get();

      if (nist80053Controls.exists) {
        const nist80053ControlsData = nist80053Controls.data() || {};

        // Process NIST 800-53 controls
        Object.keys(nist80053ControlsData).forEach(key => {
          const globalControl = nist80053ControlsData[key];
          // Try to get customer-specific data for this control if it exists
          const customerControl = customerSoaData[key] || {};

          controlsData.push({
            id: key,
            controlID: globalControl.controlID || key,
            name: globalControl.controlName || '',
            description: globalControl.controlDescription || '',
            frameworkId: 'nist800-53',
            framework: 'NIST 800-53',
            frameworkRaw: 'NIST 800-53',
            applicable: customerControl.controlApplicable === true,
            justification: customerControl.controlJustification || globalControl.controlJustification || '',
            status: customerControl.implementationStatus ||
              (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
            owner: customerControl.owner || businessSponsorName
          });
        });
      }

      console.log('[soaService.tsx] NIST 800-53 controls:', controlsData);
      return controlsData;
    } catch (error) {
      console.error('Error fetching NIST 800-53 controls:', error);
      return [];
    }
  };

/*
 * Fetches NIST CSF 2.0 Controls from firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of NIST CSF 2.0 controls
*/
const fetchNISTCSFControls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();
    
    // Check if NIST CSF is enabled
    const complianceModulesDoc = await db.collection('customers')
      .doc(companyID)
      .collection('companySettings')
      .doc('complianceModules')
      .get();

    const moduleData = complianceModulesDoc.data();
    const isEnabled = moduleData?.nistcsf?.Enabled === true || 
                     moduleData?.nistcsfEnabled === true ||
                     (moduleData?.nistcsf && moduleData.nistcsf !== false);

    console.log('[soaService.tsx] NIST CSF module check:', { isEnabled, moduleData });

    if (!complianceModulesDoc.exists || !isEnabled) {
      console.log('[soaService.tsx] NIST CSF module not enabled, returning empty controls');
      return [];
    }

    // Get global NIST CSF controls
    const nistcsfControls = await db.collection('compliance').doc('nist-csf-rev-2.0').get();

    if (nistcsfControls.exists) {
      const nistcsfControlsData = nistcsfControls.data() || {};

      // Process NIST CSF controls
      Object.keys(nistcsfControlsData).forEach(key => {
        const globalControl = nistcsfControlsData[key];
        // Try to get customer-specific data for this control if it exists
        const customerControl = customerSoaData[key] || {};

        controlsData.push({
          id: key,
          controlID: globalControl.controlID || key,
          name: globalControl.controlName || '',
          description: globalControl.controlDescription || '',
          frameworkId: 'nist-csf',
          framework: 'NIST CSF',
          frameworkRaw: 'NIST CSF',
          applicable: customerControl.controlApplicable === true,
          justification: customerControl.controlJustification || globalControl.controlJustification || '',
          status: customerControl.implementationStatus ||
            (customerControl.controlApplicable === true ? 'Fully implemented' : 'Not implemented'),
          owner: customerControl.owner || businessSponsorName
        });
      });
    }

    console.log('[soaService.tsx] NIST CSF controls:', controlsData);
    return controlsData;
  } catch (error) {
    console.error('Error fetching NIST CSF controls:', error);
    return [];
  }
};

/**
 * Fetches Starter controls from Firestore
 * @param companyID The company ID to fetch controls for
 * @param customerSoaData The customer SOA document data
 * @returns Promise containing an array of Starter controls
 */
const fetchStarterControls = async (companyID: string, customerSoaData: any = {}): Promise<Control[]> => {
  try {
    const controlsData: Control[] = [];
    const businessSponsorName = await getBusinessSponsorName();

    // Check if this is a starter account
    const companyDoc = await db.collection('customers').doc(companyID).get();
    const customerPlan = companyDoc.exists ? companyDoc.data()?.customerPlan : undefined;

    if (customerPlan === 'starter' || customerPlan === 'free') {
      // Get starter controls
      const starterControls = await db.collection('compliance').doc('starterControls').get();

      if (starterControls.exists) {
        const starterControlsData = starterControls.data() || {};

        // Process starter controls
        Object.keys(starterControlsData).forEach(key => {
          const globalControl = starterControlsData[key];
          // Try to get customer-specific data for this control if it exists
          const customerControl = customerSoaData[key] || {};

          controlsData.push({
            id: key,
            controlID: globalControl.controlID || key,
            name: globalControl.controlName || '',
            description: globalControl.controlDescription || '',
            frameworkId: 'starter',
            framework: 'Starter',
            frameworkRaw: 'Starter',
            applicable: customerControl.controlApplicable === true || true, // Starter controls are applicable by default
            justification: customerControl.controlJustification || globalControl.controlJustification || '',
            status: customerControl.implementationStatus || 'Not implemented',
            owner: customerControl.owner || businessSponsorName
          });
        });
      }
    }

    return controlsData;
  } catch (error) {
    console.error('Error fetching Starter controls:', error);
    return [];
  }
};

/**
 * Fetches controls data from Firestore
 * @param companyID The company ID to fetch controls for
 * @param frameworks Optional array of framework IDs to filter by
 * @param isModuleEnabled Optional function to check if a module is enabled
 * @returns Promise containing controls and version information
 */
export const fetchControlsData = async (companyID: string, frameworks?: string[], isModuleEnabled?: (moduleName: string) => boolean): Promise<{ controls: Control[]; versionInfo: { number: string; lastUpdated: Date } }> => {
  try {
    // Controls array to store all controls
    const controlsData: Control[] = [];

    // Version information
    let versionInfo = {
      number: '1.0.0',
      lastUpdated: new Date()
    };

    // Get customer SOA document - needed for version info and control data
    const customerSoaDoc = await db.collection('customers').doc(companyID).collection('product').doc('policyPackages').collection('ISO27001keyDocuments').doc('ismsSoa').get();
    const customerSoaData = customerSoaDoc.exists ? customerSoaDoc.data() || {} : {};

    // Extract version information if available
    if (customerSoaDoc.exists && customerSoaData.version) {
      const versionKeys = Object.keys(customerSoaData.version);
      if (versionKeys.length > 0) {
        const latestVersion = customerSoaData.version[versionKeys[versionKeys.length - 1]];

        // Get version number with fallback
        const versionNumber = latestVersion?.versionNumber || '1.0.0';

        // Handle date with fallbacks for different formats and missing data
        let lastUpdated = new Date();
        try {
          if (latestVersion?.dateUpdated) {
            // If it's a Firebase timestamp (has toDate method)
            if (typeof latestVersion.dateUpdated.toDate === 'function') {
              lastUpdated = latestVersion.dateUpdated.toDate();
            }
            // If it's a JavaScript Date object
            else if (latestVersion.dateUpdated instanceof Date) {
              lastUpdated = latestVersion.dateUpdated;
            }
            // If it's a timestamp (number)
            else if (typeof latestVersion.dateUpdated === 'number') {
              lastUpdated = new Date(latestVersion.dateUpdated);
            }
            // If it's an ISO string
            else if (typeof latestVersion.dateUpdated === 'string') {
              lastUpdated = new Date(latestVersion.dateUpdated);
            }
          }
        } catch (dateError) {
          console.warn('Error parsing version date:', dateError);
          // Keep using the default date
        }

        versionInfo = {
          number: versionNumber,
          lastUpdated: lastUpdated
        };
      }
    }

    // Determine which frameworks to fetch
    const frameworksToFetch = frameworks || [
      'qgov-is18',
      'iso27001',
      'ism',
      'soc2',
      'disp',
      'smb1001',
      'privacyAct',
      'essential8',
      'nist800-53',
      'nist-csf',
      'iso27701',
      'iso42001',
      'starter'
    ];
    const fetchPromises: Promise<Control[]>[] = [];

    // Add fetch functions for requested frameworks
    if (frameworksToFetch.includes('qgov-is18') && isModuleEnabled('qgov-is18')) {
      console.log('[soaService.tsx] QGOV IS18 module is enabled');
      fetchPromises.push(fetchQGOVIS18Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] QGOV IS18 module is disabled');
    }

    if (frameworksToFetch.includes('iso27001') && isModuleEnabled('ISO 27001')) {
      console.log('[soaService.tsx] ISO 27001 module is enabled');
      fetchPromises.push(fetchISO27001Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] ISO 27001 module is disabled');
    }

    if (frameworksToFetch.includes('ism') && isModuleEnabled('ISM')) {
      console.log('[soaService.tsx] ISM module is enabled');
      fetchPromises.push(fetchISMControls(companyID));
    } else {
      console.log('[soaService.tsx] ISM module is disabled');
    }

    if (frameworksToFetch.includes('soc2') && isModuleEnabled('SOC 2')) {
      console.log('[soaService.tsx] SOC 2 module is enabled');
      fetchPromises.push(fetchSOC2Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] SOC 2 module is disabled');
    }

    if (frameworksToFetch.includes('disp') && isModuleEnabled('DISP')) {
      console.log('[soaService.tsx] DISP module is enabled');
      fetchPromises.push(fetchDISPControls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] DISP module is disabled');
    }

    if (frameworksToFetch.includes('smb1001') && (isModuleEnabled('SMB 1001') || isModuleEnabled('SMB1001'))) {
      console.log('[soaService.tsx] SMB 1001 module is enabled');
      fetchPromises.push(fetchSMB1001Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] SMB 1001 module is disabled');
    }

    if (frameworksToFetch.includes('privacyAct') && isModuleEnabled('Privacy Act')) {
      console.log('[soaService.tsx] Privacy Act module is enabled');
      fetchPromises.push(fetchPrivacyActControls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] Privacy Act module is disabled');
    }

    if (frameworksToFetch.includes('essential8') && isModuleEnabled('Essential 8')) {
      console.log('[soaService.tsx] Essential 8 module is enabled');
      fetchPromises.push(fetchEssential8Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] Essential 8 module is disabled');
    }

    // if (frameworksToFetch.includes('nist') && (isModuleEnabled('NIST') || isModuleEnabled('nist'))) {
    //   console.log('[soaService.tsx] NIST module is enabled');
    //   fetchPromises.push(fetchNISTControls(companyID, customerSoaData));
    // } else {
    //   console.log('[soaService.tsx] NIST module is disabled');
    // }

    if (frameworksToFetch.includes('iso27701') && isModuleEnabled('ISO 27701')) {
      console.log('[soaService.tsx] ISO 27701 module is enabled');
      fetchPromises.push(fetchISO27701Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] ISO 27701 module is disabled');
    }

    if (frameworksToFetch.includes('iso42001') && isModuleEnabled('ISO 42001')) {
      console.log('[soaService.tsx] ISO 42001 module is enabled');
      fetchPromises.push(fetchISO42001Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] ISO 42001 module is disabled');
    }

    if (frameworksToFetch.includes('nist-csf') && (isModuleEnabled('NIST CSF') || isModuleEnabled('nistcsf'))) {
      console.log('[soaService.tsx] NIST CSF module is enabled');
      fetchPromises.push(fetchNISTCSFControls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] NIST CSF module is disabled');
    }

    if (frameworksToFetch.includes('nist800-53') && (isModuleEnabled('NIST 800-53') || isModuleEnabled('nist80053'))) {
      console.log('[soaService.tsx] NIST 800-53 module is enabled');
      fetchPromises.push(fetchNIST80053Controls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] NIST 800-53 module is disabled');
    }

    if (frameworksToFetch.includes('starter')) {
      console.log('[soaService.tsx] Starter module is enabled');
      fetchPromises.push(fetchStarterControls(companyID, customerSoaData));
    } else {
      console.log('[soaService.tsx] Starter module is disabled');
    }

    // Fetch all controls in parallel for better performance
    const controlArrays = await Promise.all(fetchPromises);

    // Combine all control arrays
    controlArrays.forEach(controls => {
      controlsData.push(...controls);
    });

    return { controls: controlsData, versionInfo };
  } catch (error) {
    console.error('Error fetching controls data:', error);
    throw error;
  }
};

// Calculate framework statistics
const calculateFrameworkStats = (frameworks: Framework[], controls: Control[]): FrameworkStats[] => {
  return frameworks.map((framework) => {
    // First get all controls for this framework
    console.log(`[calculateFrameworkStats] Processing controls for framework ${framework.name} (${framework.id})`);
    const frameworkControls = controls.filter((c) => c.frameworkId === framework.id);
    
    // Log total controls for debugging
    console.log(`[calculateFrameworkStats] Processing ${framework.name}: ${frameworkControls.length} total controls`);
    
    // Initialize counters
    const counts = {
      implemented: 0,
      partiallyImplemented: 0,
      notImplemented: 0,
      notApplicable: 0,
      unknown: 0
    };
    
    // Process each control exactly once
    frameworkControls.forEach(control => {
      const status = (control.status || '').toLowerCase();
      const applicableValue = control.applicable as boolean | string | undefined;
      
      // Log each control for debugging
      console.log(`[calculateFrameworkStats] Control ${control.controlID || control.id}: `, {
        status,
        applicable: applicableValue
      });
      
      // Check status in order of specificity
      if (status === 'not applicable' || 
          applicableValue === false || 
          (typeof applicableValue === 'string' && applicableValue.toLowerCase() === 'false')) {
        counts.notApplicable++;
      } 
      else if (status === 'partially implemented') {
        counts.partiallyImplemented++;
      }
      else if (status === 'not implemented') {
        counts.notImplemented++;
      }
      else if (status === 'unknown') {
        counts.unknown++;
      }
      else if (status === 'fully implemented' || 
               status === 'implemented' || 
               applicableValue === true || 
               (typeof applicableValue === 'string' && applicableValue.toLowerCase() === 'true')) {
        counts.implemented++;
      }
      else {
        // Default to not implemented if no status is set
        counts.notImplemented++;
      }
    });
    
    // Log the final counts for this framework
    console.log(`[calculateFrameworkStats] ${framework.name} final counts:`, counts);
    
    // Verify the total adds up
    const totalCounted = Object.values(counts).reduce((sum, count) => sum + count, 0);
    if (totalCounted !== frameworkControls.length) {
      console.warn(`[calculateFrameworkStats] Warning: Count mismatch! Expected ${frameworkControls.length} controls, but counted ${totalCounted}`);
    }
    
    return {
      id: framework.id,
      name: framework.name,
      stats: counts
    };
  });
};

/**
   * Gets the complete SOA data including controls, frameworks, and statistics
   * @param companyID The company ID to fetch SOA data for
   * @returns Promise containing the complete SOA data
   */
/**
 * Updates a control in the SOA document
 * @param companyID The company ID
 * @param control The control to update
 * @returns Promise containing the updated version information
 */
export async function updateControlStatus(companyID: string, control: Control): Promise<VersionInfo | null> {
  try {
    // Reference to the SOA document
    const soaDocRef = doc(db, 'customers', companyID, 'product', 'policyPackages', 'ISO27001keyDocuments', 'ismsSoa');

    // Get current SOA document
    const soaDoc = await getDoc(soaDocRef);
    const soaData = soaDoc.exists() ? soaDoc.data() || {} : {};

    // Check if control exists
    const existingControl = soaData[control.id];
    const controlApplicableChanged = existingControl?.controlApplicable !== control.applicable;

    // Prepare control data to update
    const controlData = {
      controlID: control.controlID,
      controlName: control.name,
      controlDesc: control.description,
      framework: control.frameworkId || control.framework,
      controlApplicable: control.applicable,
      controlJustification: control.justification,
      implementationStatus: control.status,
      lastModified: new Date(),
      modifiedBy: getAuthUser()?.email || 'unknown',
      owner: control.owner
    };

    // Route updates through MCM service so both MCM and SOA are kept in sync
    await MCMService.updateControl(
      control.id,
      {
        controlName: control.name,
        controlDescription: control.description,
        standard: control.frameworkId || (control as any).framework,
        controlApplicable: control.applicable,
        status: control.status as any,
        owner: control.owner,
        controlJustification: control.justification,
      },
      companyID
    );

    // Update version history
    const versionUpdate = await updateVersionAndHistory(
      soaDocRef,  // Pass the document reference, not the control object
      control.id,
      controlData,
      `Updated control ${control.controlID}: ${control.name}`
    );

    // Handle assurance tasks if applicable status changed
    if (controlApplicableChanged) {
      // This part would need to be implemented if assurance task handling is required
      console.log('Control applicable status changed, assurance tasks would be handled here');
    }

    return {
      number: versionUpdate.versionNumber,
      lastUpdated: versionUpdate.timestamp
    };
  } catch (error) {
    console.error('[soaService] Error updating control:', error);
    throw new Error(`Failed to update control: ${(error as Error).message}`);
  }
}

const updateVersionAndHistory = async (
  customerSoaDoc: DocumentReference,
  controlID: string,
  controlData: any,
  updateComments: string = 'Updated Version'
): Promise<{ versionNumber: string; versionKey: string; timestamp: any }> => {
  const soaSnapshot = await getDoc(customerSoaDoc);

  if (!soaSnapshot.exists) {
    throw new Error('SOA Not found');
  }

  const soaData = soaSnapshot.data() || {};
  const versionData = soaData.version || {};
  const versionKeys = Object.keys(versionData);

  // Determine current version
  const currentVersion = versionKeys.length > 0 ? versionData[versionKeys[versionKeys.length - 1]].versionNumber : '1.0.0';

  // Calculate next version
  const nextVersion = (() => {
    const parts = currentVersion.split('.');
    if (parts.length === 2) {
      return `${parts[0]}.${parseInt(parts[1]) + 1}`;
    } else if (parts.length === 3) {
      return `${parts[0]}.${parts[1]}.${parseInt(parts[2]) + 1}`;
    }
    return '1.0.1';
  })();

  // Calculate next version key
  const nextVersionKey = versionKeys.length > 0 ? (parseInt(versionKeys[versionKeys.length - 1]) + 1).toString() : '1';

  // Create version update data
  const versionUpdate = {
    version: {
      ...versionData,
      [nextVersionKey]: {
        dateUpdated: FirebaseTimestamp.now(),
        updateComments,
        updatesMade: {
          [controlID]: controlData
        },
        updatedBy: getAuthUser()?.uid || 'unknown',
        versionNumber: nextVersion
      }
    }
  };

  // Update the document
  await setDoc(customerSoaDoc, versionUpdate, { merge: true });

  return {
    versionNumber: nextVersion,
    versionKey: nextVersionKey,
    timestamp: FirebaseTimestamp.now()
  };
};

export async function getSOAData(companyID: string, isModuleEnabled: (moduleName: string) => boolean): Promise<SOAData | null> {
  console.log(`%c[soaService] ⚡ FETCH STARTED: getSOAData for company: ${companyID}`, 'color: rgb(224, 175, 0); font-weight: bold; font-size: 14px;');

  try {

    // If companyID is undefined, return null
    if (!companyID) {
      return emptySOAData;
    }

    // Get frameworks
    console.log('%c[soaService.tsx] Fetching frameworks data...', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;');
    const frameworks = await fetchFrameworksData(companyID, isModuleEnabled);
    console.log('%c[soaService.tsx] Frameworks data:', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;', frameworks);
    
    // Get controls and version info
    console.log('%c[soaService.tsx] Fetching controls data...', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;');
    const { controls, versionInfo } = await fetchControlsData(companyID, undefined, isModuleEnabled);
    console.log('%c[soaService.tsx] Controls data:', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;', controls);

    // Calculate stats based on the fetched data
    console.log('%c[soaService.tsx] Calculating stats...', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;');
    const stats = calculateFrameworkStats(frameworks, controls);
    console.log('%c[soaService.tsx] Stats:', 'color:rgb(224, 175, 0); font-weight: bold; font-size: 14px;', stats);
    
    return {
      controls,
      frameworks,
      stats,
      version: versionInfo
    };
  } catch (error) {
    console.error('[soaService] Error fetching SOA data:', error);
    throw new Error('Failed to fetch SOA data');
  }
}

/**
 * Gets SOA exclusions - controls that are not implemented or not applicable
 * @param companyID The company ID to fetch exclusions for
 * @param isModuleEnabled Function to check if a module is enabled
 * @returns Promise containing controls that are not implemented/not applicable
 */
export async function getSOAExclusions(companyID: string, isModuleEnabled: (moduleName: string) => boolean): Promise<Control[]> {
  try {
    const soaData = await getSOAData(companyID, isModuleEnabled);
    if (!soaData || !soaData.controls) {
      return [];
    }

    // Filter controls that are not implemented or not applicable
    return soaData.controls.filter(control => {
      const status = control.status?.toLowerCase();
      if (control.applicable === false || (control?.applicable as unknown as string) === 'false' ) {
        return true;
      }
      return status === 'not implemented' || status === 'not applicable';
    });
  } catch (error) {
    console.error('[soaService] Error fetching SOA exclusions:', error);
    return [];
  }
}

/**
 * Gets the complete version history for the SOA document
 * @param companyID The company ID to fetch version history for
 * @returns Promise containing an array of version history entries sorted by date (newest first)
 */
export async function getSOAVersionHistory(companyID: string): Promise<VersionHistoryEntry[]> {
  try {
    if (!companyID) {
      return [];
    }

    // Get customer SOA document
    const customerSoaDoc = await db.collection('customers').doc(companyID).collection('product').doc('policyPackages').collection('ISO27001keyDocuments').doc('ismsSoa').get();
    
    if (!customerSoaDoc.exists) {
      return [];
    }

    const customerSoaData = customerSoaDoc.data() || {};
    const versionData = customerSoaData.version || {};
    const versionKeys = Object.keys(versionData);

    if (versionKeys.length === 0) {
      return [];
    }

    // Convert version data to array of VersionHistoryEntry
    const versionHistory: VersionHistoryEntry[] = versionKeys.map(key => {
      const version = versionData[key];
      
      // Parse date with fallbacks
      let dateUpdated = new Date();
      try {
        if (version?.dateUpdated) {
          if (typeof version.dateUpdated.toDate === 'function') {
            dateUpdated = version.dateUpdated.toDate();
          } else if (version.dateUpdated instanceof Date) {
            dateUpdated = version.dateUpdated;
          } else if (typeof version.dateUpdated === 'number') {
            dateUpdated = new Date(version.dateUpdated);
          } else if (typeof version.dateUpdated === 'string') {
            dateUpdated = new Date(version.dateUpdated);
          }
        }
      } catch (dateError) {
        console.warn('[soaService] Error parsing version date:', dateError);
      }

      return {
        versionKey: key,
        versionNumber: version?.versionNumber || '1.0.0',
        dateUpdated,
        updatedBy: version?.updatedBy || 'Unknown',
        updateComments: version?.updateComments || '',
        updatesMade: version?.updatesMade || {}
      };
    });

    // Sort by date (newest first)
    return versionHistory.sort((a, b) => b.dateUpdated.getTime() - a.dateUpdated.getTime());
  } catch (error) {
    console.error('[soaService] Error fetching SOA version history:', error);
    return [];
  }
}